import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Button, Container, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import React, { useCallback, useEffect, useRef } from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { LANDING_PRIMARY_PLAYGROUND_HREF } from "#/features/homePage/lib/landingPlaygroundDemos";
import { HomeLandingHeroPreview } from "#/features/homePage/ui/landing/HomeLandingHeroPreview";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { useMobileLayout } from "#/shared/hooks";
import { LogoModelView } from "#/shared/ui/molecules/LogoModelView";
type HomeLandingHeroProps = {
  LL: TranslationFunctions;
};
const HERO_MODEL_BASE_AZIMUTH = Math.PI / 4.6;
const HERO_MODEL_BASE_POLAR = Math.PI / 2.5;
const HERO_MODEL_MAX_AZIMUTH_OFFSET = 0.28;
const HERO_MODEL_MAX_POLAR_OFFSET = 0.18;
const HERO_MODEL_DAMPING = 0.08;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const AmbientBackground = () => {
  const $ = _c(8);
  const theme = useTheme();
  let t0;
  if ($[0] !== theme.appDesign.accent) {
    t0 = alpha(theme.appDesign.accent, 0.16);
    $[0] = theme.appDesign.accent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== theme.appDesign.accentSoft) {
    t1 = alpha(theme.appDesign.accentSoft, 0.12);
    $[2] = theme.appDesign.accentSoft;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== theme.appDesign.surfaceLow) {
    t2 = alpha(theme.appDesign.surfaceLow, 0.12);
    $[4] = theme.appDesign.surfaceLow;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `radial-gradient(circle at 18% 18%, ${t0} 0%, transparent 42%), radial-gradient(circle at 82% 12%, ${t1} 0%, transparent 36%), linear-gradient(180deg, ${t2} 0%, transparent 55%)`;
  let t4;
  if ($[6] !== t3) {
    t4 = <Box sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100vh",
      background: t3,
      pointerEvents: "none"
    }} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const HomeLandingHero: React.FC<HomeLandingHeroProps> = t0 => {
  const $ = _c(111);
  const {
    LL
  } = t0;
  const theme = useTheme();
  const isMobileLayout = useMobileLayout();
  const controlsRef = useRef(null);
  const currentAzimuthRef = useRef(HERO_MODEL_BASE_AZIMUTH);
  const currentPolarRef = useRef(HERO_MODEL_BASE_POLAR);
  const targetAzimuthRef = useRef(HERO_MODEL_BASE_AZIMUTH);
  const targetPolarRef = useRef(HERO_MODEL_BASE_POLAR);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (azimuth, polar) => {
      const controls = controlsRef.current;
      if (!controls) {
        return;
      }
      controls.setAzimuthalAngle(azimuth);
      controls.setPolarAngle(polar);
      controls.update();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const applyModelAngles = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      currentAzimuthRef.current = HERO_MODEL_BASE_AZIMUTH;
      currentPolarRef.current = HERO_MODEL_BASE_POLAR;
      targetAzimuthRef.current = HERO_MODEL_BASE_AZIMUTH;
      targetPolarRef.current = HERO_MODEL_BASE_POLAR;
      applyModelAngles(HERO_MODEL_BASE_AZIMUTH, HERO_MODEL_BASE_POLAR);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const setModelRestingPosition = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      targetAzimuthRef.current = HERO_MODEL_BASE_AZIMUTH;
      targetPolarRef.current = HERO_MODEL_BASE_POLAR;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const resetModelAngles = t3;
  let t4;
  if ($[3] !== isMobileLayout) {
    t4 = (clientX, clientY) => {
      if (isMobileLayout) {
        return;
      }
      if (!controlsRef.current || window.innerWidth === 0 || window.innerHeight === 0) {
        return;
      }
      const xRatio = clientX / window.innerWidth - 0.5;
      const yRatio = clientY / window.innerHeight - 0.5;
      targetAzimuthRef.current = HERO_MODEL_BASE_AZIMUTH - xRatio * HERO_MODEL_MAX_AZIMUTH_OFFSET * 2;
      targetPolarRef.current = clamp(HERO_MODEL_BASE_POLAR + yRatio * HERO_MODEL_MAX_POLAR_OFFSET * 2, Math.PI / 2.9, Math.PI / 1.9);
    };
    $[3] = isMobileLayout;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const updateModelFromPointer = t4;
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setModelRestingPosition();
    };
    t6 = [setModelRestingPosition];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] !== isMobileLayout || $[8] !== updateModelFromPointer) {
    t7 = () => {
      if (isMobileLayout) {
        setModelRestingPosition();
        return;
      }
      let frameId = 0;
      const animateModel = () => {
        const nextAzimuth = currentAzimuthRef.current + (targetAzimuthRef.current - currentAzimuthRef.current) * HERO_MODEL_DAMPING;
        const nextPolar = currentPolarRef.current + (targetPolarRef.current - currentPolarRef.current) * HERO_MODEL_DAMPING;
        currentAzimuthRef.current = nextAzimuth;
        currentPolarRef.current = nextPolar;
        applyModelAngles(nextAzimuth, nextPolar);
        frameId = window.requestAnimationFrame(animateModel);
      };
      const handleWindowMouseMove = event => {
        updateModelFromPointer(event.clientX, event.clientY);
      };
      const handlePointerExit = () => {
        resetModelAngles();
      };
      window.addEventListener("mousemove", handleWindowMouseMove);
      window.addEventListener("blur", handlePointerExit);
      document.documentElement.addEventListener("mouseleave", handlePointerExit);
      frameId = window.requestAnimationFrame(animateModel);
      return () => {
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("blur", handlePointerExit);
        document.documentElement.removeEventListener("mouseleave", handlePointerExit);
        window.cancelAnimationFrame(frameId);
      };
    };
    t8 = [applyModelAngles, isMobileLayout, resetModelAngles, setModelRestingPosition, updateModelFromPointer];
    $[7] = isMobileLayout;
    $[8] = updateModelFromPointer;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      position: "relative",
      overflow: "visible",
      bgcolor: "background.default",
      pt: {
        xs: 14,
        md: 18
      },
      pb: {
        xs: 8,
        md: 10
      }
    };
    t10 = <AmbientBackground />;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      position: "absolute",
      top: {
        xs: -88,
        sm: -170,
        md: -280
      },
      right: {
        xs: -118,
        sm: -180,
        md: -320
      },
      width: {
        xs: 460,
        sm: 700,
        md: 1180,
        lg: 1360
      },
      height: {
        xs: 460,
        sm: 700,
        md: 1180,
        lg: 1360
      },
      opacity: {
        xs: 0.12,
        md: 0.2
      },
      filter: "blur(0.4px)",
      pointerEvents: "none",
      maskImage: "radial-gradient(circle at 48% 50%, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 32%, rgba(0,0,0,0.46) 66%, transparent 90%)"
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [1, 2.6, 22];
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Box sx={t11}><LogoModelView controlsRef={controlsRef} interactive={false} cameraPosition={t12} cameraFov={38} target={[0, 0.75, 0]} distanceRange={[18, 28]} /></Box>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        lg: "minmax(0, 5fr) minmax(0, 7fr)"
      },
      gap: {
        xs: 4,
        md: 6
      },
      alignItems: "center"
    };
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      minWidth: 0
    };
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      position: "relative",
      zIndex: 1
    };
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      maxWidth: 700,
      fontSize: {
        xs: "2.15rem",
        sm: "3.4rem",
        md: "4.8rem"
      },
      lineHeight: {
        xs: 1.08,
        md: 1.02
      }
    };
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== LL) {
    t19 = LL.HOME_LANDING_TITLE();
    $[21] = LL;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t19) {
    t20 = <Typography variant="h1" sx={t18}>{t19}</Typography>;
    $[23] = t19;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      maxWidth: 620,
      fontSize: {
        xs: "1rem",
        md: "1.08rem"
      },
      lineHeight: 1.75
    };
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== LL) {
    t22 = LL.HOME_LANDING_SUBTITLE();
    $[26] = LL;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t22) {
    t23 = <Typography variant="body1" color="text.secondary" sx={t21}>{t22}</Typography>;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      xs: "column",
      sm: "row"
    };
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      pt: 1
    };
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[32] = t26;
  } else {
    t26 = $[32];
  }
  let t27;
  if ($[33] !== LL) {
    t27 = LL.TRY_IT_OUT_NOW();
    $[33] = LL;
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  let t28;
  if ($[35] !== t27) {
    t28 = <Button component={Link} data-testid="cta-to-playground" href={LANDING_PRIMARY_PLAYGROUND_HREF} variant="contained" size="large" sx={t26}>{t27}</Button>;
    $[35] = t27;
    $[36] = t28;
  } else {
    t28 = $[36];
  }
  let t29;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      width: {
        xs: "100%",
        sm: "auto"
      }
    };
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== LL) {
    t30 = LL.BROWSE_PROJECTS();
    $[38] = LL;
    $[39] = t30;
  } else {
    t30 = $[39];
  }
  let t31;
  if ($[40] !== t30) {
    t31 = <Button component={Link} href="/playground?view=browse" variant="outlined" size="large" sx={t29}>{t30}</Button>;
    $[40] = t30;
    $[41] = t31;
  } else {
    t31 = $[41];
  }
  let t32;
  if ($[42] !== t28 || $[43] !== t31) {
    t32 = <Stack direction={t24} spacing={1.5} sx={t25}>{t28}{t31}</Stack>;
    $[42] = t28;
    $[43] = t31;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      xs: "column",
      sm: "row"
    };
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      xs: "flex-start",
      sm: "center"
    };
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== LL) {
    t35 = LL.HOME_HERO_FAQ_LINK();
    $[47] = LL;
    $[48] = t35;
  } else {
    t35 = $[48];
  }
  let t36;
  if ($[49] !== t35) {
    t36 = <Stack direction={t33} spacing={1} alignItems={t34}><Button component={Link} href="#faq" variant="text" color="inherit">{t35} →</Button></Stack>;
    $[49] = t35;
    $[50] = t36;
  } else {
    t36 = $[50];
  }
  let t37;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      pt: 1
    };
    $[51] = t37;
  } else {
    t37 = $[51];
  }
  let t38;
  if ($[52] !== theme.appDesign.surfaceHigh) {
    t38 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[52] = theme.appDesign.surfaceHigh;
    $[53] = t38;
  } else {
    t38 = $[53];
  }
  let t39;
  if ($[54] !== theme.appDesign.outline) {
    t39 = alpha(theme.appDesign.outline, 0.14);
    $[54] = theme.appDesign.outline;
    $[55] = t39;
  } else {
    t39 = $[55];
  }
  const t40 = `1px solid ${t39}`;
  let t41;
  if ($[56] !== t38 || $[57] !== t40) {
    t41 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t38,
      border: t40
    };
    $[56] = t38;
    $[57] = t40;
    $[58] = t41;
  } else {
    t41 = $[58];
  }
  let t42;
  if ($[59] !== LL) {
    t42 = LL.HOME_LANG_JS_TITLE();
    $[59] = LL;
    $[60] = t42;
  } else {
    t42 = $[60];
  }
  let t43;
  if ($[61] !== t42) {
    t43 = <Typography variant="caption" color="text.secondary">{t42}</Typography>;
    $[61] = t42;
    $[62] = t43;
  } else {
    t43 = $[62];
  }
  let t44;
  if ($[63] !== t41 || $[64] !== t43) {
    t44 = <Box sx={t41}>{t43}</Box>;
    $[63] = t41;
    $[64] = t43;
    $[65] = t44;
  } else {
    t44 = $[65];
  }
  let t45;
  if ($[66] !== theme.appDesign.surfaceHigh) {
    t45 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[66] = theme.appDesign.surfaceHigh;
    $[67] = t45;
  } else {
    t45 = $[67];
  }
  let t46;
  if ($[68] !== theme.appDesign.outline) {
    t46 = alpha(theme.appDesign.outline, 0.14);
    $[68] = theme.appDesign.outline;
    $[69] = t46;
  } else {
    t46 = $[69];
  }
  const t47 = `1px solid ${t46}`;
  let t48;
  if ($[70] !== t45 || $[71] !== t47) {
    t48 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t45,
      border: t47
    };
    $[70] = t45;
    $[71] = t47;
    $[72] = t48;
  } else {
    t48 = $[72];
  }
  let t49;
  if ($[73] !== LL) {
    t49 = LL.HOME_LANG_PYTHON_TITLE();
    $[73] = LL;
    $[74] = t49;
  } else {
    t49 = $[74];
  }
  let t50;
  if ($[75] !== t49) {
    t50 = <Typography variant="caption" color="text.secondary">{t49}</Typography>;
    $[75] = t49;
    $[76] = t50;
  } else {
    t50 = $[76];
  }
  let t51;
  if ($[77] !== t48 || $[78] !== t50) {
    t51 = <Box sx={t48}>{t50}</Box>;
    $[77] = t48;
    $[78] = t50;
    $[79] = t51;
  } else {
    t51 = $[79];
  }
  let t52;
  if ($[80] !== theme.appDesign.surfaceHigh) {
    t52 = alpha(theme.appDesign.surfaceHigh, 0.82);
    $[80] = theme.appDesign.surfaceHigh;
    $[81] = t52;
  } else {
    t52 = $[81];
  }
  let t53;
  if ($[82] !== theme.appDesign.outline) {
    t53 = alpha(theme.appDesign.outline, 0.14);
    $[82] = theme.appDesign.outline;
    $[83] = t53;
  } else {
    t53 = $[83];
  }
  const t54 = `1px solid ${t53}`;
  let t55;
  if ($[84] !== t52 || $[85] !== t54) {
    t55 = {
      px: 1.25,
      py: 0.75,
      borderRadius: 99,
      bgcolor: t52,
      border: t54
    };
    $[84] = t52;
    $[85] = t54;
    $[86] = t55;
  } else {
    t55 = $[86];
  }
  let t56;
  if ($[87] !== LL) {
    t56 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[87] = LL;
    $[88] = t56;
  } else {
    t56 = $[88];
  }
  let t57;
  if ($[89] !== t56) {
    t57 = <Typography variant="caption" color="text.secondary">{t56}</Typography>;
    $[89] = t56;
    $[90] = t57;
  } else {
    t57 = $[90];
  }
  let t58;
  if ($[91] !== t55 || $[92] !== t57) {
    t58 = <Box sx={t55}>{t57}</Box>;
    $[91] = t55;
    $[92] = t57;
    $[93] = t58;
  } else {
    t58 = $[93];
  }
  let t59;
  if ($[94] !== t44 || $[95] !== t51 || $[96] !== t58) {
    t59 = <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap={true} sx={t37}>{t44}{t51}{t58}</Stack>;
    $[94] = t44;
    $[95] = t51;
    $[96] = t58;
    $[97] = t59;
  } else {
    t59 = $[97];
  }
  let t60;
  if ($[98] !== t20 || $[99] !== t23 || $[100] !== t32 || $[101] !== t36 || $[102] !== t59) {
    t60 = <Box sx={t16}><Stack spacing={2.5} sx={t17}>{t20}{t23}{t32}{t36}{t59}</Stack></Box>;
    $[98] = t20;
    $[99] = t23;
    $[100] = t32;
    $[101] = t36;
    $[102] = t59;
    $[103] = t60;
  } else {
    t60 = $[103];
  }
  let t61;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = {
      minWidth: 0
    };
    $[104] = t61;
  } else {
    t61 = $[104];
  }
  let t62;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = {
      position: "relative",
      zIndex: 1,
      minWidth: 0,
      mx: {
        xs: "auto",
        lg: 0
      }
    };
    $[105] = t62;
  } else {
    t62 = $[105];
  }
  let t63;
  if ($[106] !== LL) {
    t63 = <Box sx={t61}><Box sx={t62}><HomeLandingHeroPreview LL={LL} /></Box></Box>;
    $[106] = LL;
    $[107] = t63;
  } else {
    t63 = $[107];
  }
  let t64;
  if ($[108] !== t60 || $[109] !== t63) {
    t64 = <Box component="section" sx={t9}>{t10}{t13}<Container maxWidth="xl" sx={t14}><Box sx={t15}>{t60}{t63}</Box></Container></Box>;
    $[108] = t60;
    $[109] = t63;
    $[110] = t64;
  } else {
    t64 = $[110];
  }
  return t64;
};